/**
 * Shared HTML sanitization — the single render-boundary defense against
 * stored XSS for any content rendered via `dangerouslySetInnerHTML`.
 *
 * Per CLAUDE.md security conventions: every `dangerouslySetInnerHTML` sink
 * MUST route its HTML through one of these helpers. Do not call
 * `DOMPurify.sanitize` ad-hoc in components — funnel through here so the
 * allowed-tag/attribute policy lives in exactly one place and can be audited.
 *
 * Two modes:
 *   - sanitizeRichText(): user-authored TipTap content (status narratives,
 *     project section bodies, goal descriptions) and trusted bundled HTML
 *     (EULA). Strips scripts, event handlers, iframes, and javascript: URLs;
 *     keeps standard formatting.
 *   - sanitizeEmbed(): third-party oEmbed markup (e.g. YouTube). Same script/
 *     handler stripping, but PRESERVES allowlisted <iframe> embeds and their
 *     embed-related attributes so legitimate video embeds still render.
 *
 * Note: <script> tags inserted via innerHTML never execute in the browser
 * anyway, so stripping them in the embed mode is not a functional regression
 * for script-hydrated embeds — those never worked through this path.
 */

import DOMPurify from 'dompurify';

/**
 * Sanitize user-authored or trusted rich text (TipTap output, bundled HTML).
 * Uses DOMPurify's default HTML profile: no scripts, no event handlers,
 * no iframes, no unsafe URI protocols.
 */
export function sanitizeRichText(html: string | null | undefined): string {
  if (!html) return '';
  return DOMPurify.sanitize(html, { USE_PROFILES: { html: true } });
}

/**
 * Sanitize third-party embed markup while preserving allowlisted iframes.
 * Scripts and event handlers are still stripped; javascript:/data: URLs in
 * iframe src are rejected by DOMPurify's protocol checks. The server-side
 * embed allowlist remains the primary control over which providers are
 * embedded — this is defense-in-depth at the render boundary.
 */
export function sanitizeEmbed(html: string | null | undefined): string {
  if (!html) return '';
  return DOMPurify.sanitize(html, {
    ADD_TAGS: ['iframe'],
    ADD_ATTR: [
      'allow',
      'allowfullscreen',
      'frameborder',
      'scrolling',
      'referrerpolicy',
      'loading',
      'sandbox',
      'title',
      'width',
      'height',
    ],
  });
}
